import { GET_WITHDRAWAL_SCREEN_BOTTOMSHEET } from '../appConstants/APIEndpoint';
import { requestApiGet } from './index';
import type {
  INameMismatchBottomSheet,
  IScreenNudge,
  IWithdrawalGateResponse,
} from '../interfaces/Withdrawal';

/**
 * The pre-withdrawal gate.
 *
 * Android calls this on the wallet's Withdraw button and branches before
 * navigating (`BaseWalletFragment.kt:2178-2186`): location restriction, then a
 * screen nudge, then a KYC name mismatch, otherwise proceed.
 *
 * Action-triggered rather than a mount query, since it runs on the tap.
 */

export type TWithdrawalGate =
  | { kind: 'LOCATION'; location: IWithdrawalGateResponse }
  | { kind: 'NUDGE'; nudge: IScreenNudge }
  | { kind: 'MISMATCH'; mismatch: INameMismatchBottomSheet }
  | { kind: 'PROCEED' }
  | { kind: 'ERROR'; error: string };

export const WITHDRAWAL_GATE_SCREEN_WALLET = 'WALLET';

export async function fetchWithdrawalGate(
  screenName: string = WITHDRAWAL_GATE_SCREEN_WALLET,
): Promise<TWithdrawalGate> {
  const res = await requestApiGet<{
    nameMismatchBottomSheet?: INameMismatchBottomSheet;
  }>(GET_WITHDRAWAL_SCREEN_BOTTOMSHEET, { params: { screenName } });

  const response = res?.response;
  if (!response?.success) {
    // Do not fall through to PROCEED: this is the only compliance gate before
    // a money transfer, and Android likewise declines to proceed on failure.
    return { kind: 'ERROR', error: response?.error || 'Could not verify' };
  }

  // The location fields sit flat on `response`, beside `data`.
  const gate = response as unknown as IWithdrawalGateResponse;

  // Android's order. Each branch is gated on its field being present, so an
  // unexpected shape degrades to PROCEED rather than blocking withdrawal.
  if (gate.errorType) return { kind: 'LOCATION', location: gate };
  if (gate.nudge) return { kind: 'NUDGE', nudge: gate.nudge };

  const mismatch = response?.data?.nameMismatchBottomSheet;
  if (mismatch) return { kind: 'MISMATCH', mismatch };

  return { kind: 'PROCEED' };
}
